'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { getDate, getHours, getMinutes, getMonth, getYear } from 'date-fns';
import { getContainer } from '@framework/di/getContainer';
import { AlertManager } from '@dealroadshow/uikit-alert';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import ScheduleDemoRepository from '@framework/ui/Organisms/ScheduleDemo/ScheduleDemoRepository';
import { IScheduleDemoPayload } from '@framework/ui/Organisms/ScheduleDemo/vo/ScheduleDemoPayload';
import getZonedUTCDate from '@framework/date-helpers/getZonedUTCDate';
import getUnixTimestamp from '@framework/date-helpers/getUnixTimestamp';
import getDateFromUnixTime from '@framework/date-helpers/getDateFromUnixTime';
import { useTimeZoneContext } from '@framework/timezone/TimeZoneContext';
import { ITimeZoneOption } from '@framework/timezone/helpers';

const useScheduleDemo = () => {
  const container = getContainer();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  // @ts-ignore
  const [submittedFormValues, setSubmittedFormValues] = useState<IScheduleDemoPayload>(null);

  const { getTimeZoneList, timeZoneList, isFetching } = useTimeZoneContext();

  useEffect(() => {
    getTimeZoneList();
  }, []);

  const submitScheduleDemoForm = async (
    formData: Omit<IScheduleDemoPayload, 'demoDateTime' | 'timeZone'> & {
      dateAt: number,
      timeAt: number,
      phone: {
        code: string,
        value: string,
      },
      timeZone: ITimeZoneOption,
    },
    isPublic: boolean,
  ) => {
    const {
      dateAt: dateAtUnix,
      timeAt: timeAtUnix,
      phone,
      timeZone,
      notes = '',
      ...restFormData
    } = formData;

    const selectedTimeZone = timeZoneList.find(({ id }) => id === timeZone.value);
    const dateAt = getDateFromUnixTime(dateAtUnix);
    const timeAt = getDateFromUnixTime(timeAtUnix);

    const payload: IScheduleDemoPayload = {
      notes,
      phone: phone.value,
      // @ts-ignore
      timeZone: selectedTimeZone,
      demoDateTime: getUnixTimestamp(getZonedUTCDate(
        // @ts-ignore
        selectedTimeZone.timeZone,
        new Date(
          getYear(dateAt),
          getMonth(dateAt),
          getDate(dateAt),
          getHours(timeAt),
          getMinutes(timeAt),
        ),
      )).toString(),
      ...restFormData,
    };

    setIsSubmitting(true);

    try {
      const scheduleDemoRepository = container.get<ScheduleDemoRepository>(ScheduleDemoRepository);

      if (isPublic) {
        await scheduleDemoRepository.requestScheduledDemoPublic(payload);
      } else {
        await scheduleDemoRepository.requestScheduledDemoPrivate(payload);
      }

      setSubmittedFormValues(payload);
      setIsSubmitted(true);
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = () => {
    setIsSubmitting(false);
    setIsSubmitted(false);
    // @ts-ignore
    setSubmittedFormValues(null);
  };

  return {
    isFetching,
    isSubmitting,
    isSubmitted,
    submittedFormValues,
    submitScheduleDemoForm,
    reset,
  };
};

type ScheduleDemoContextType = ReturnType<typeof useScheduleDemo>;

// @ts-ignore
export const ScheduleDemoContext = createContext<ScheduleDemoContextType>(null);

export const useScheduleDemoContext = (): ScheduleDemoContextType => {
  const context = useContext<ScheduleDemoContextType>(ScheduleDemoContext);
  if (!context) {
    throw new Error('useScheduleDemoContext must be used within a ScheduleDemoContextProvider');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const ScheduleDemoContextProvider = ({ children }: IProps) => (
  <ScheduleDemoContext.Provider value={ { ...useScheduleDemo() } }>
    { children }
  </ScheduleDemoContext.Provider>
);

export default ScheduleDemoContextProvider;
